/**
 * Form helpers for ManualReferralModal: validation, payload building, and
 * the professional-role narrowing shared by both.
 *
 * Feature 101 party model:
 * - referring: existing user/contact from search | new provisional contact |
 *   bQuest Concierge
 * - referred: the entity locked from the profile page, or a new provisional
 *   contact ("not this person" escape); in edit mode (feature 117) any
 *   professional from search
 * - on behalf of (optional): existing user/contact from search | new
 *   provisional contact
 */

import type {
  ManualReferralPrefill,
  UpdateManualReferralPayload,
  ManualReferralEntitySearchItem,
  ManualReferralProfessionalRole,
} from '../../types/manual-referral.type';
import type { NewCareSeekerFormState } from './NewCareSeekerFields';
import type { NewProfessionalContactFormState } from './NewProfessionalContactFields';

export type LockedReferredEntity = ManualReferralEntitySearchItem & {
  role: ManualReferralProfessionalRole;
};

/** A search result narrowed to a professional (FP/SP) role. */
export type ReferringProfessional = ManualReferralEntitySearchItem & {
  role: ManualReferralProfessionalRole;
};

export type ReferringMode = 'search' | 'new' | 'concierge';
export type ReferredMode = 'locked' | 'search' | 'new';
export type OnBehalfOfMode = 'none' | 'search' | 'new';
export type CareSeekerMode = 'search' | 'new' | 'none';

/** Shared styling for the section mode-toggle buttons. */
export const modeButtonClass = (isActive: boolean) =>
  `rounded-md border px-3 py-1.5 text-sm ${
    isActive
      ? 'border-[#026857] bg-[#026857]/10 font-medium text-[#026857]'
      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;

type ProfessionalContactErrors = Partial<Record<keyof NewProfessionalContactFormState, string>>;

export type FormErrors = {
  referringProfessional?: string;
  referred?: string;
  newReferringContact?: ProfessionalContactErrors;
  newReferredContact?: ProfessionalContactErrors;
  onBehalfOf?: string;
  newOnBehalfOfContact?: ProfessionalContactErrors;
  careSeeker?: string;
  submit?: string;
  newCareSeeker?: Partial<Record<keyof NewCareSeekerFormState, string>>;
};

export type ManualReferralFormState = {
  referringMode: ReferringMode;
  referringProfessional: ManualReferralEntitySearchItem | null;
  newReferringContact: NewProfessionalContactFormState;
  referredMode: ReferredMode;
  // Edit mode only: the referred professional picked from search.
  referred: ManualReferralEntitySearchItem | null;
  newReferredContact: NewProfessionalContactFormState;
  onBehalfOfMode: OnBehalfOfMode;
  onBehalfOf: ManualReferralEntitySearchItem | null;
  newOnBehalfOfContact: NewProfessionalContactFormState;
  careSeekerMode: CareSeekerMode;
  careSeeker: ManualReferralEntitySearchItem | null;
  newCareSeeker: NewCareSeekerFormState;
};

export const emptyNewCareSeeker: NewCareSeekerFormState = {
  firstName: '',
  lastName: '',
  email: '',
  sendInvitationEmail: false,
};

export const roleLabel = (role: ManualReferralProfessionalRole) =>
  role === 'SERVICE_PROVIDER' ? 'Service Provider' : 'Financial Advisor';

export const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isReferringProfessional = (
  entity: ManualReferralEntitySearchItem | null,
): entity is ReferringProfessional =>
  entity !== null && (entity.role === 'FINANCIAL_ADVISOR' || entity.role === 'SERVICE_PROVIDER');

const validateProfessionalContact = (
  value: NewProfessionalContactFormState,
): ProfessionalContactErrors => {
  const errors: ProfessionalContactErrors = {};
  if (!value.firstName.trim()) errors.firstName = 'First name is required.';
  if (!value.lastName.trim()) errors.lastName = 'Last name is required.';
  if (!value.role) errors.role = 'Select a role.';
  return errors;
};

const setContactErrors = (
  errors: FormErrors,
  key: 'newReferringContact' | 'newReferredContact' | 'newOnBehalfOfContact',
  value: NewProfessionalContactFormState,
) => {
  const contactErrors = validateProfessionalContact(value);
  if (Object.keys(contactErrors).length > 0) {
    errors[key] = contactErrors;
  }
};

/** The referred professional the form currently points at (null for a new contact). */
const currentReferred = (
  state: ManualReferralFormState,
  referredEntity: LockedReferredEntity | null,
): ManualReferralEntitySearchItem | null =>
  state.referredMode === 'locked'
    ? referredEntity
    : state.referredMode === 'search'
      ? state.referred
      : null;

export const validateForm = (
  state: ManualReferralFormState,
  referredEntity: LockedReferredEntity | null,
): FormErrors => {
  const errors: FormErrors = {};
  const referred = currentReferred(state, referredEntity);

  if (state.referredMode === 'search' && !isReferringProfessional(state.referred)) {
    errors.referred = 'Select the professional who received the referral.';
  }

  if (state.referringMode === 'search') {
    if (!isReferringProfessional(state.referringProfessional)) {
      errors.referringProfessional = 'Select the professional who made the referral.';
    } else if (
      referred &&
      state.referringProfessional.kind === referred.kind &&
      state.referringProfessional.id === referred.id
    ) {
      errors.referringProfessional =
        'The referring professional must be different from the referred professional.';
    }
  } else if (state.referringMode === 'new') {
    setContactErrors(errors, 'newReferringContact', state.newReferringContact);
  }

  if (state.referredMode === 'new') {
    setContactErrors(errors, 'newReferredContact', state.newReferredContact);
  }

  if (state.onBehalfOfMode === 'search' && !state.onBehalfOf) {
    errors.onBehalfOf = 'Select who the referral was made on behalf of.';
  } else if (state.onBehalfOfMode === 'new') {
    setContactErrors(errors, 'newOnBehalfOfContact', state.newOnBehalfOfContact);
  }

  if (state.careSeekerMode === 'search' && !state.careSeeker) {
    errors.careSeeker =
      'Select an existing care seeker, add a new one, or choose "No care seeker".';
  }
  if (state.careSeekerMode === 'new') {
    errors.newCareSeeker = validateNewCareSeeker(state.newCareSeeker);
    if (Object.keys(errors.newCareSeeker).length === 0) {
      delete errors.newCareSeeker;
    }
  }
  return errors;
};

const validateNewCareSeeker = (value: NewCareSeekerFormState) => {
  const errors: Partial<Record<keyof NewCareSeekerFormState, string>> = {};
  if (!value.firstName.trim()) errors.firstName = 'First name is required.';
  if (!value.lastName.trim()) errors.lastName = 'Last name is required.';
  if (value.sendInvitationEmail && !value.email.trim()) {
    errors.email = 'Email is required to send an invitation.';
  }
  return errors;
};

const contactPayload = (value: NewProfessionalContactFormState) => ({
  firstName: value.firstName.trim(),
  lastName: value.lastName.trim(),
  ...(value.email.trim() ? { email: value.email.trim() } : {}),
  role: value.role as ManualReferralProfessionalRole,
});

export const buildPayload = ({
  state,
  referredEntity,
  referralOccurredAt,
  notes,
}: {
  state: ManualReferralFormState;
  referredEntity: LockedReferredEntity | null;
  referralOccurredAt: string;
  notes: string;
}): UpdateManualReferralPayload => {
  const payload: UpdateManualReferralPayload = {};

  if (state.referringMode === 'concierge') {
    payload.isConciergeReferral = true;
  } else if (state.referringMode === 'new') {
    payload.newReferringContact = contactPayload(state.newReferringContact);
  } else if (state.referringProfessional) {
    if (state.referringProfessional.kind === 'CONTACT') {
      payload.referringContactId = state.referringProfessional.id;
    } else {
      payload.referringUserId = state.referringProfessional.id;
      payload.referringRole = state.referringProfessional.role as ManualReferralProfessionalRole;
    }
  }

  const referred = currentReferred(state, referredEntity);
  if (state.referredMode === 'new') {
    payload.newReferredContact = contactPayload(state.newReferredContact);
  } else if (referred?.kind === 'CONTACT') {
    payload.referredContactId = referred.id;
  } else if (referred) {
    payload.referredUserId = referred.id;
    payload.referredRole = referred.role as ManualReferralProfessionalRole;
  }

  if (state.onBehalfOfMode === 'search' && state.onBehalfOf) {
    if (state.onBehalfOf.kind === 'CONTACT') {
      payload.onBehalfOfContactId = state.onBehalfOf.id;
    } else {
      payload.onBehalfOfUserId = state.onBehalfOf.id;
    }
  } else if (state.onBehalfOfMode === 'new') {
    payload.newOnBehalfOfContact = contactPayload(state.newOnBehalfOfContact);
  }

  if (state.careSeekerMode === 'new') {
    payload.newCareSeeker = {
      firstName: state.newCareSeeker.firstName.trim(),
      lastName: state.newCareSeeker.lastName.trim(),
      ...(state.newCareSeeker.email.trim() ? { email: state.newCareSeeker.email.trim() } : {}),
      sendInvitationEmail: state.newCareSeeker.sendInvitationEmail,
    };
  } else if (state.careSeekerMode === 'none') {
    payload.noCareSeeker = true;
  } else if (state.careSeeker?.kind === 'CONTACT') {
    // Edit mode: keep the referral's own provisional care-seeker contact.
    payload.manualCareSeekerContactId = state.careSeeker.id;
  } else if (state.careSeeker) {
    payload.careSeekerUserId = state.careSeeker.id;
  }
  if (referralOccurredAt) payload.referralOccurredAt = referralOccurredAt;
  if (notes.trim()) payload.notes = notes.trim();
  return payload;
};

/** Feature 117: edit-mode form state from the GET prefill. */
export const formStateFromPrefill = (
  prefill: ManualReferralPrefill,
  empty: ManualReferralFormState,
): ManualReferralFormState => ({
  ...empty,
  referringMode: prefill.isConciergeReferral ? 'concierge' : 'search',
  referringProfessional: prefill.referring,
  referredMode: 'search',
  referred: prefill.referred,
  onBehalfOfMode: prefill.onBehalfOf ? 'search' : 'none',
  onBehalfOf: prefill.onBehalfOf,
  careSeekerMode: prefill.careSeeker ? 'search' : 'none',
  careSeeker: prefill.careSeeker,
});
